<script setup>
import { getI18n } from '@/plugins/i18n'

import * as dataGrid from '@/composables/data-grid'
import { menuRoutesEnum } from '@/router'
import { codeNameDisplayExpr } from '@/composables/label'
import { useODataSource } from '@/composables/data-grid'
import { usePortalStore } from '@/stores/portal.store'
import { useRegistryStore } from '@/stores/registry.store'
import { useFocusRestore } from '@/composables/focus-restore'

import GridDropDownBox from '@/components/shared/GridDropDownBox.vue'

const emit = defineEmits({
  valueChanged: (e) => true,
})
const props = defineProps({
  data: {
    type: Object,
    default: null,
  },
  showClearButton: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  exclude: {
    type: Number,
    required: false,
  },
  value: {
    type: Number,
    default: null,
  },
})

const registryStore = useRegistryStore()
const portalStore = usePortalStore()
const i18n = getI18n()

const data = props.value || props.data?.value
const vehicleSearchExpr = ['name', 'vin', 'plate_number', 'customer_full_name']

const defaultDataSource = useODataSource(registryStore.api.getAbsoluteUrl() + registryStore.api.vehiclesSearchUrl, {
  filter: props.exclude ? ['id', '<>', props.exclude] : null,
  searchExpr: vehicleSearchExpr,
})

const resolveSearchOptions = (value) => {
  const normalizedValue = String(value ?? '').trim()
  const isVehicleId = /^\d+$/.test(normalizedValue)

  return {
    searchExpr: isVehicleId ? ['id'] : vehicleSearchExpr,
    searchOperation: isVehicleId ? '=' : 'contains',
    searchValue: isVehicleId ? Number(normalizedValue) : value,
  }
}

const extractInfo = (vehicleId) => {
  if (vehicleId) {
    const vehicle = registryStore.vehicles.find(v => v.id === vehicleId)
    const data = [
      { key: i18n.t('common.protocol'), value: vehicle?.id },
      { key: i18n.t('common.description'), value: vehicle?.name },
      { key: i18n.t('common.color'), value: vehicle?.color },
      { key: i18n.t('common.engineCode'), value: vehicle?.engine_code },
      { key: i18n.t('common.vehicleFuelType'), value: codeNameDisplayExpr(registryStore.getLocalizedVehicleFuelTypes().find(ft => ft.id === vehicle?.fuel_type)) },
      { key: i18n.t('common.km'), value: vehicle?.km },
      { key: i18n.t('common.plate'), value: vehicle?.plate_number },
      { key: i18n.t('common.status'), value: codeNameDisplayExpr(registryStore.getLocalizedVehicleStatusTypes().find(s => s.id === vehicle?.status)) },
      { key: i18n.t('common.vehicleType'), value: codeNameDisplayExpr(portalStore.getLocalizedVehicleTypes().find(s => s.id === vehicle?.vehicle_type)) },
      { key: i18n.t('print.chassis'), value: vehicle?.vin },
      { key: i18n.t('common.supplierBrand'), value: codeNameDisplayExpr(registryStore.getSupplierBrandById(vehicle?.supplier_brand_ref)) },
      { key: i18n.t('common.enrollmentDate'), value: vehicle?.enrollment_date },
      { key: i18n.t('common.saleDate'), value: vehicle?.sale_date },
      { key: i18n.t('common.revisionDueDate'), value: vehicle?.revision_due_date },
    ]

    return data
  }
}

const valueChanged = (e) => {
  emit('valueChanged', { value: e })
  props.data?.setValue(e)
}

const { rootRef } = useFocusRestore('vehicle')
</script>

<template>
  <div ref="rootRef">
    <grid-drop-down-box
      :disabled="props.disabled"
      :value="data"
      :data-source="defaultDataSource"
      :resolve-search-options="resolveSearchOptions"
      :show-clear-button="props.showClearButton"
      :columns="[
        { dataField: 'id', dataType: 'number', filterOperations: ['=', '<>'], caption: $t('common.protocol') },
        { dataField: 'vin', caption: $t('print.chassis') },
        { dataField: 'plate_number', caption: $t('common.plate'), sortOrder: 'asc',},
        { dataField: 'name', caption: $t('common.description') },
        { dataField: 'vehicle_type', caption: $t('common.vehicleType'), lookup: dataGrid.useLookup({ store: portalStore.getLocalizedVehicleTypes() }), },
        { dataField: 'status', caption: $t('common.status'), lookup: dataGrid.useLookup({ store: registryStore.getLocalizedVehicleStatusTypes() }), },
        { dataField: 'engine_size', caption: $t('common.engineSize') },
        { dataField: 'color', caption: $t('common.color') },
        { dataField: 'customer_full_name', caption: $t('common.name') },
        { dataField: 'customer_phone', caption: $t('common.phone') },
      ]"
      :drop-down-options="{ width: '56rem', height: '30rem' }"
      :options="{ target: menuRoutesEnum.VEHICLES, routeParam: props.data?.data?.vehicle?.id, extractInfo: extractInfo(data) }"
      @value-changed="valueChanged"
    />
  </div>
</template>
